import { localizeReportValue } from "./reportValueCatalog";

/**
 * I18N-C3-C2 — Sheet24 verified composite presenters.
 *
 * Presentation only.
 *
 * Rules:
 * - Never parse/split/normalize/replace canonical Sheet24 strings.
 * - Rebuild authoritative canonical outputs only from separately supplied
 *   canonical components.
 * - Require byte-exact equality before English presentation.
 * - Unknown/missing/mismatched components fail closed to canonical.
 * - "—" is a valid authoritative sentinel.
 * - Number-group strings such as "3、4" and "2、5、8" remain opaque strings.
 */

const STEM_EN = Object.freeze({
  "甲": "Jia",
  "乙": "Yi",
  "丙": "Bing",
  "丁": "Ding",
  "戊": "Wu",
  "己": "Ji",
  "庚": "Geng",
  "辛": "Xin",
  "壬": "Ren",
  "癸": "Gui",
});

function localizeElementOrSentinel(value) {
  if (value === "—") return value;

  const localized = localizeReportValue(
    "en",
    "element",
    value,
  );

  return localized === value
    ? undefined
    : localized;
}

function validLifeNumber(value) {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 9
  );
}

/**
 * C7 coreBasis
 *
 * Authoritative:
 * 五行需求：${fiveElementNeed}\n日主：${dayMasterStem}（${dayMasterElement}）
 */
export function presentSheet24CoreBasis(
  locale,
  canonical,
  {
    fiveElementNeed,
    dayMasterStem,
    dayMasterElement,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof fiveElementNeed !== "string" ||
    typeof dayMasterStem !== "string" ||
    typeof dayMasterElement !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `五行需求：${fiveElementNeed}\n` +
    `日主：${dayMasterStem}（${dayMasterElement}）`;

  if (canonical !== expectedCanonical) return canonical;

  const stem = STEM_EN[dayMasterStem];

  if (stem === undefined) return canonical;

  const element = localizeElementOrSentinel(
    dayMasterElement,
  );

  if (element === undefined) return canonical;

  let need;

  if (fiveElementNeed === "") {
    need = "No specific support element";
  } else {
    need = localizeReportValue(
      "en",
      "fiveElementNeed",
      fiveElementNeed,
    );

    if (need === fiveElementNeed) return canonical;
  }

  return (
    `Five-Element support need: ${need}\n` +
    `Day Master: ${stem} (${element})`
  );
}

/**
 * E12 ruleDigest
 *
 * Authoritative:
 * 核心：${r5CoreNumbers}
 * 辅助：${r7AuxNumbers}
 * 生命数字：${r3LifeNumber}
 */
export function presentSheet24RuleDigest(
  locale,
  canonical,
  {
    r5CoreNumbers,
    r7AuxNumbers,
    r3LifeNumber,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof r5CoreNumbers !== "string" ||
    typeof r7AuxNumbers !== "string" ||
    !validLifeNumber(r3LifeNumber)
  ) {
    return canonical;
  }

  const expectedCanonical =
    `核心：${r5CoreNumbers}\n` +
    `辅助：${r7AuxNumbers}\n` +
    `生命数字：${r3LifeNumber}`;

  if (canonical !== expectedCanonical) return canonical;

  return (
    `Core: ${r5CoreNumbers}\n` +
    `Supporting: ${r7AuxNumbers}\n` +
    `Life Number: ${r3LifeNumber}`
  );
}

/**
 * G18 combinationStructure
 */
export function presentSheet24CombinationStructure(
  locale,
  canonical,
  {
    r5CoreNumbers,
    r7AuxNumbers,
    r3LifeNumber,
    r4LifeNumberElement,
    r8ControllingElement,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof r5CoreNumbers !== "string" ||
    typeof r7AuxNumbers !== "string" ||
    typeof r4LifeNumberElement !== "string" ||
    typeof r8ControllingElement !== "string" ||
    !validLifeNumber(r3LifeNumber)
  ) {
    return canonical;
  }

  const overlap =
    r4LifeNumberElement === r8ControllingElement;

  const expectedCanonical = overlap
    ? `${r5CoreNumbers}为主体\n${r7AuxNumbers}作辅助，${r3LifeNumber}保留1位`
    : `${r5CoreNumbers}为主体\n${r7AuxNumbers}作辅助，可加入${r3LifeNumber}`;

  if (canonical !== expectedCanonical) return canonical;

  return overlap
    ? `${r5CoreNumbers} as primary\n${r7AuxNumbers} as support; keep one occurrence of ${r3LifeNumber}`
    : `${r5CoreNumbers} as primary\n${r7AuxNumbers} as support; ${r3LifeNumber} may be included`;
}

/**
 * A45 summaryUsage
 */
export function presentSheet24SummaryUsage(
  locale,
  canonical,
  {
    r5CoreNumbers,
    r7AuxNumbers,
    r3LifeNumber,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof r5CoreNumbers !== "string" ||
    typeof r7AuxNumbers !== "string" ||
    !validLifeNumber(r3LifeNumber)
  ) {
    return canonical;
  }

  const expectedCanonical =
    `把 ${r5CoreNumbers} 作为主要偏好、${r7AuxNumbers} 作为辅助；` +
    `数字 ${r3LifeNumber} 作为个人锚点。` +
    `只在实际条件相近的选项中作最后筛选。`;

  if (canonical !== expectedCanonical) return canonical;

  return (
    `Use ${r5CoreNumbers} as the primary preference and ${r7AuxNumbers} as support; ` +
    `use ${r3LifeNumber} as a personal anchor. ` +
    `Apply this only as a final preference when practical options are otherwise similar.`
  );
}

/**
 * A51 integratedRecommendation
 */
export function presentSheet24IntegratedRecommendation(
  locale,
  canonical,
  {
    r2Element,
    r3LifeNumber,
    r4LifeNumberElement,
    r5CoreNumbers,
    r6GeneratingElement,
    r7AuxNumbers,
    r8ControllingElement,
    r9ReduceNumbers,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  const requiredStrings = [
    r2Element,
    r4LifeNumberElement,
    r5CoreNumbers,
    r6GeneratingElement,
    r7AuxNumbers,
    r8ControllingElement,
    r9ReduceNumbers,
  ];

  if (
    requiredStrings.some(
      (value) => typeof value !== "string",
    ) ||
    !validLifeNumber(r3LifeNumber)
  ) {
    return canonical;
  }

  const overlap =
    r4LifeNumberElement === r8ControllingElement;

  const expectedCanonical =
    `综合建议：以 ${r5CoreNumbers}（${r2Element}）为核心多用号码，` +
    `${r7AuxNumbers}（${r6GeneratingElement}）为辅助；` +
    `生命数字 ${r3LifeNumber}（${r4LifeNumberElement}）` +
    (
      overlap
        ? "保留一位即可，不宜重复堆叠"
        : "可作为个人锚点"
    ) +
    `。少用号码为 ${r9ReduceNumbers}（${r8ControllingElement}）；` +
    `“少用”代表避免过度集中，并非绝对禁用。`;

  if (canonical !== expectedCanonical) return canonical;

  const r2En = localizeElementOrSentinel(r2Element);
  const r4En = localizeElementOrSentinel(
    r4LifeNumberElement,
  );
  const r6En = localizeElementOrSentinel(
    r6GeneratingElement,
  );
  const r8En = localizeElementOrSentinel(
    r8ControllingElement,
  );

  if (
    r2En === undefined ||
    r4En === undefined ||
    r6En === undefined ||
    r8En === undefined
  ) {
    return canonical;
  }

  const lifeNumberClause = overlap
    ? "should be kept to one occurrence rather than repeated"
    : "may serve as a personal anchor";

  return (
    `Overall recommendation: use ${r5CoreNumbers} (${r2En}) as the primary preferred numbers, ` +
    `with ${r7AuxNumbers} (${r6En}) as support; ` +
    `Life Number ${r3LifeNumber} (${r4En}) ${lifeNumberClause}. ` +
    `Reduced-use numbers: ${r9ReduceNumbers} (${r8En}); ` +
    `“reduced-use” means avoiding excessive concentration, not an absolute prohibition.`
  );
}
